<template>
  <h1>Terms of Use</h1>

  <p><strong>Last updated: June 26, 2026</strong></p>

  <p>
    These Terms of Use are provided for general information only and do not
    constitute legal advice. They govern your access to and use of SharkFoto's
    services. Please read them carefully before using the platform.
  </p>

  <h3 id="id-1.-preamble">1. Preamble</h3>
  <p>
    1.1. SharkFoto Inc. (hereinafter referred to as the "Operator," "SharkFoto,"
    "we," "us," or "our") operates the website
    <a href="https://sharkfoto.com/">sharkfoto.com</a>, an AI-powered media
    platform that enables users to generate, edit, and transform images, video,
    and audio using artificial intelligence, including AI Effects, AI Models, and
    a suite of free tools (collectively, the "Platform" or the "Services").
  </p>
  <p>
    1.2. These Terms of Use (the "Terms") apply to all users of the Platform
    (hereinafter: the "User," "you," or "your"). By creating an account,
    accessing, or using the Platform, you agree to be bound by these Terms. If you
    do not agree, you must not use the Platform.
  </p>
  <p>
    1.3. These Terms incorporate by reference our
    <a href="/company/trust/privacy/">Privacy Policy</a> and our
    <a href="/company/trust/refund-policy/">Refund Policy</a>.
  </p>

  <h3 id="id-2.-rights-of-use">2. Rights of Use</h3>
  <p>
    2.1. Subject to these Terms, the Operator grants the User a limited,
    non-exclusive, non-transferable, revocable license to access and use the
    Platform.
  </p>
  <p>
    2.2. The User owns the content they upload or provide to the Platform (the
    "Input") as well as the results generated by the Platform from that Input (the
    "Output"). The User may use their Input and Output for any lawful purpose,
    including commercial purposes, subject to these Terms and applicable law.
  </p>
  <p>
    2.3. The Operator has the right to restrict or suspend a User's access to the
    Platform, with or without notice, where reasonably necessary to enforce these
    Terms, comply with law, or protect the Platform and its users.
  </p>
  <p>
    2.4. The User agrees that the Operator may store the User's IP address and
    related technical data to operate the Platform, prevent abuse, and verify
    extensive or automated use of the Platform.
  </p>

  <h3 id="id-3.-ai-generated-content">
    3. AI-Generated Content: Ownership &amp; License
  </h3>
  <p>
    3.1. <strong>Your Input.</strong> You retain all rights you hold in the Input
    you provide. You represent and warrant that you have all rights, licenses, and
    consents necessary to upload and process your Input through the Platform.
  </p>
  <p>
    3.2. <strong>Your Output.</strong> As between you and SharkFoto, you own the
    Output generated from your Input. SharkFoto does not claim ownership of your
    Output.
  </p>
  <p>
    3.3. <strong>No training on your content.</strong> We do not use your Input or
    your Output to train or fine-tune our AI models.
  </p>
  <p>
    3.4. <strong>Third-party processing.</strong> To provide the Services, your
    Input may be processed by third-party AI model providers (for example, OpenAI,
    Google, and others). Your use of the Platform is also subject to the
    handling described in our <a href="/company/trust/privacy/">Privacy Policy</a>.
  </p>
  <p>
    3.5. <strong>Output provided "AS-IS".</strong> AI-generated Output is provided
    on an "AS-IS" basis. Because generative models can produce similar results for
    different users, we do not guarantee that any Output is original, free of
    third-party rights, non-infringing, or cleared for any particular commercial
    use. You are solely responsible for reviewing your Output and ensuring it is
    appropriate and lawful for your intended use.
  </p>

  <h3 id="id-4.-acceptable-use">4. Acceptable Use &amp; Prohibited Content</h3>
  <p>
    4.1. The User undertakes to refrain from any actions that endanger the
    functionality, security, or operation of the Platform. In particular, the User
    is prohibited from scanning or testing the vulnerabilities of the Platform,
    bypassing security or access controls, introducing malware, reverse
    engineering or attempting to extract the underlying AI models, or engaging in
    automated abuse, scraping, or load/stress testing without authorization.
  </p>
  <p>
    4.2. The User must not use the Platform to generate, upload, distribute, or
    otherwise process content that:
  </p>
  <ul>
    <li><p>is unlawful or facilitates illegal activity;</p></li>
    <li>
      <p>
        infringes the intellectual property, privacy, or other rights of any third
        party;
      </p>
    </li>
    <li>
      <p>
        depicts a real, identifiable person's likeness without consent, or
        constitutes deceptive deepfake or impersonation abuse;
      </p>
    </li>
    <li><p>sexualizes minors or exploits children in any way;</p></li>
    <li><p>is fraudulent, deceptive, or intended to mislead;</p></li>
    <li>
      <p>
        harasses, threatens, defames, or promotes hatred or violence against
        individuals or groups;
      </p>
    </li>
    <li>
      <p>
        constitutes spam, automated abuse, or otherwise interferes with the
        normal operation of the Platform.
      </p>
    </li>
  </ul>
  <p>
    4.3. Violation of this section may result in immediate suspension or
    termination of the User's account, with or without notice, and may be reported
    to the relevant authorities where required by law.
  </p>

  <h3 id="id-5.-subscriptions-credits-and-billing">
    5. Subscriptions, Credits &amp; Billing
  </h3>
  <p>
    5.1. Certain features of the Platform are offered through paid subscription
    plans and credits. Subscriptions, credits, and automatic renewals are
    processed by our third-party payment provider, Stripe. By purchasing a plan or
    credits, you authorize the applicable recurring or one-time charges.
  </p>
  <p>
    5.2. Refunds are governed by our
    <a href="/company/trust/refund-policy/">Refund Policy</a>. Please review it
    before making a purchase; the detailed eligibility rules and process are set
    out there and are not repeated in these Terms.
  </p>
  <p>
    5.3. If you believe you have been billed in error, please contact
    <a href="mailto:support@sharkfoto.com">support@sharkfoto.com</a> within 30 days
    of the charge so we can investigate the billing dispute.
  </p>

  <h3 id="id-6.-disclaimers-and-limitation-of-liability">
    6. Disclaimers &amp; Limitation of Liability
  </h3>
  <p>
    6.1. <strong>"AS-IS" / "AS-AVAILABLE".</strong> The Platform and all AI Output
    are provided on an "AS-IS" and "AS-AVAILABLE" basis. To the maximum extent
    permitted by applicable law, the Operator disclaims all warranties, whether
    express or implied, including implied warranties of merchantability, fitness
    for a particular purpose, non-infringement, accuracy, and availability.
  </p>
  <p>
    6.2. The Operator is not liable for the speed of the Platform, its
    availability, data loss, or the correctness, originality, or suitability of
    any Output. Except where caused intentionally or through gross negligence, and
    except in cases of death or personal injury or where liability cannot be
    excluded under applicable law, the Operator's liability is excluded.
  </p>
  <p>
    6.3. <strong>Cap on liability.</strong> To the maximum extent permitted by
    applicable law, the Operator's total aggregate liability arising out of or
    relating to the Platform shall not exceed the amount actually paid by the User
    to SharkFoto for the relevant Services during the period giving rise to the
    claim.
  </p>

  <h3 id="id-7.-intellectual-property-and-copyright">
    7. Intellectual Property &amp; Copyright (DMCA)
  </h3>
  <p>
    7.1. The Operator retains all right, title, and interest in and to the
    Platform, its software, AI models, brand, trademarks, and all related
    intellectual property. Nothing in these Terms grants the User any rights in the
    Operator's intellectual property other than the limited license set out in
    Section 2.
  </p>
  <p>
    7.2. <strong>Copyright complaints.</strong> If you believe content on the
    Platform infringes your copyright, please send a notice to
    <a href="mailto:support@sharkfoto.com">support@sharkfoto.com</a> that includes:
  </p>
  <ul>
    <li><p>your contact information;</p></li>
    <li>
      <p>identification of the copyrighted work you claim has been infringed;</p>
    </li>
    <li>
      <p>
        identification of the allegedly infringing material and its location on
        the Platform;
      </p>
    </li>
    <li>
      <p>
        a statement that you have a good-faith belief that the use is not
        authorized by the rights holder, its agent, or the law;
      </p>
    </li>
    <li>
      <p>
        a statement, under penalty of perjury, that the information in your notice
        is accurate and that you are the rights holder or authorized to act on the
        rights holder's behalf;
      </p>
    </li>
    <li><p>your physical or electronic signature.</p></li>
  </ul>
  <p>
    7.3. <strong>Counter-notice.</strong> If your content was removed and you
    believe this was in error, you may submit a counter-notice to
    <a href="mailto:support@sharkfoto.com">support@sharkfoto.com</a> identifying
    the removed material and providing a statement, under penalty of perjury, that
    you have a good-faith belief the material was removed by mistake, together with
    your contact information and signature.
  </p>
  <p>
    7.4. <strong>Repeat infringers.</strong> We may suspend or terminate the
    accounts of Users who are determined to be repeat infringers.
  </p>

  <h3 id="id-8.-account-suspension-termination-and-your-data">
    8. Account Suspension, Termination &amp; Your Data
  </h3>
  <p>
    8.1. The Operator may suspend or terminate a User's account for violation of
    these Terms, non-payment of fees, repeated infringement, or where required by
    law.
  </p>
  <p>
    8.2. <strong>Your data and retention.</strong> You may delete your content or
    your account at any time through your account settings, or by contacting
    <a href="mailto:support@sharkfoto.com">support@sharkfoto.com</a>. Content is
    retained until you choose to delete it; we do not automatically purge it on
    your behalf. Additional details are set out in our
    <a href="/company/trust/privacy/">Privacy Policy</a>.
  </p>
  <p>
    8.3. <strong>Changes and maintenance.</strong> The Operator may modify,
    suspend, or discontinue all or part of the Platform, and may perform scheduled
    or emergency maintenance that results in temporary downtime, without liability
    for any resulting unavailability.
  </p>

  <h3 id="id-9.-governing-law-and-jurisdiction">
    9. Governing Law &amp; Jurisdiction
  </h3>
  <p>
    9.1. These Terms and any dispute or claim arising out of or in connection with
    them or their subject matter are governed by and construed in accordance with
    the laws of the Hong Kong Special Administrative Region of the People's
    Republic of China.
  </p>
  <p>
    9.2. The User and the Operator submit to the exclusive jurisdiction of the
    courts of the Hong Kong Special Administrative Region to settle any such
    dispute or claim.
  </p>

  <h3 id="id-10.-region-specific-eu-users">10. Region-Specific / EU Users</h3>
  <p>
    10.1. If you are located in the European Union or European Economic Area, you
    have certain data protection rights under the General Data Protection
    Regulation (GDPR), including rights of access, rectification, erasure,
    restriction, portability, and objection. We are committed to honoring these
    rights.
  </p>
  <p>
    10.2. For details on how we process your personal data and how to exercise
    your rights, please refer to our
    <a href="/company/trust/privacy/">Privacy Policy</a>.
  </p>

  <h3 id="id-11.-concluding-provisions">11. Concluding Provisions</h3>
  <p>
    11.1. We may update these Terms from time to time. Material changes will be
    reflected by updating the "Last updated" date above, and where appropriate we
    will provide additional notice. Your continued use of the Platform after
    changes take effect constitutes acceptance of the revised Terms.
  </p>
  <p>
    11.2. Should individual provisions of these Terms be void, unenforceable,
    and/or invalid, this shall not affect the validity of the remaining
    provisions. In such case, the parties shall replace the affected provision with
    a valid provision that most closely reflects its original economic intent.
  </p>
  <p>
    11.3. For any questions about these Terms, please contact us at
    <a href="mailto:support@sharkfoto.com">support@sharkfoto.com</a>.
  </p>
</template>

<script setup>
definePageMeta({
  layout: "company",
});

import { useHead } from "#imports";

useHead({
  title: "Terms of use | SharkFoto",
  meta: [
    {
      name: "description",
      content:
        "SharkFoto Terms of Use: license and ownership of AI-generated content, acceptable use, subscriptions and billing, disclaimers and liability, copyright/DMCA, your data, and governing law (Hong Kong SAR).",
    },
  ],
  link: [
    {
      rel: "canonical",
      href: "https://sharkfoto.com/company/trust/terms/",
    },
  ],
});
</script>
